// Unpublished Work © 2022-2025 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Box, TextField, Typography, InputAdornment} from '@deere/isg.component-library';
import ValidationInput from 'Ui/components/common/form/validation-input';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import {getLocalTimeFormats, validateDate} from 'Ui/components/manual-data/manual-data-utils';
import {getTimeFromDate, MANUAL_DATA_TIME_FORMAT} from 'Utils/time-utils';
import moment from 'moment';
import {fetchEffectDataV2} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import ReadOnlyWrapperComponent from 'Ui/components/common/form/read-only/read-only-wrapper-component';
import PinLocation from './pin-location';
import DateTimePicker from 'Ui/components/common/date-picker/date-time-picker';
import {connect} from 'react-redux';
import {fetchTeamMembers, setLoadingOperators, setNameValidation, updateDetailsField} from 'Src/ui-core/store/actions/setup-workboard';
import {MANAGE_WORKBOARDS, VIEW_WORKBOARDS} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {CircularProgress} from '@mui/material';
import {getWorkboards} from 'Services/membership-service';
import {formatWorkboards} from '../../utils/workboard-data-utils';
import useWorkboardDuplicateValidation from '../hooks/use-workboard-duplicate-validation';

const MAX_NOTE_LENGTH = 355;

function handleDetailsChange(event, setNotesError, handleChange) {
    const {
        name,
        value
    } = event.target;

    if (name === 'notes') {
        setNotesError(false);
    }

    if (name === 'notes' && value.length > MAX_NOTE_LENGTH) {
        setNotesError(true);
        return;
    }

    handleChange({
        field: name,
        value
    });
}

const WorkboardDetails = (props) => {
    const {
        details,
        handleChange,
        handleLoadingOperators,
        handleNameValidation,
        handleUpdateTeamMembers,
        isCopy,
        isEditing,
        isDraft,
        membership,
        membershipId,
        nameValidation,
        myJdPermissions,
        setValid,
        translations,
        type,
        workboardId
    } = props;

    const hasPermission = isAuthorized({
        myJdPermissions: [MANAGE_WORKBOARDS, VIEW_WORKBOARDS]
    }, {
        isMigrated: true,
        myJdPermissions
    });

    const {
        duplicateValidation,
        isTemplate
    } = useWorkboardDuplicateValidation({
        detailsDate: details.date,
        detailsName: details.name,
        handleNameValidation,
        isEditing,
        membershipId,
        nameValidation,
        type,
        workboardId,
        translations
    });

    const [workboards, setWorkboards] = React.useState([]);

    React.useEffect(() => {
        if (!membershipId) {
            return undefined;
        }

        return fetchEffectDataV2(async ({signal}) => {
            const {workboards: workboardsData} = await getWorkboards(undefined, signal);

            setWorkboards(formatWorkboards(workboardsData));
        });
    }, [membershipId]);

    const defaultDate = React.useMemo(() => {
        return membership?.properties?.workboard_default_start_time ?
            moment().startOf('day').set('hour', membership.properties.workboard_default_start_time) :
            moment();
    }, [membership?.properties?.workboard_default_start_time]);

    const {
        validatedDate, validatedTime
    } = React.useMemo(() => {
        const date = details.date === '' ? defaultDate : details.date;
        const validatedDate = validateDate(date, setValid);
        const validatedTime = validatedDate ? getTimeFromDate(validatedDate) : null;

        return {
            validatedDate: isCopy && details.date === '' ? null : validatedDate,
            validatedTime
        };
    }, [defaultDate, details.date, isCopy, setValid]);

    const [notesError, setNotesError] = React.useState(false);

    React.useEffect(() => {
        if (!isCopy && details.date === '') {
            handleChange({
                field: 'date',
                value: defaultDate.format(MANUAL_DATA_TIME_FORMAT)
            });
        }
    }, [defaultDate, details.date, handleChange, isCopy]);

    const loading = React.useMemo(() => {
        return (
            <InputAdornment position='end'>
                {details.loading && (
                    <CircularProgress
                        color='#C7C6C5'
                        enableTrackSlot
                        size={20}
                    />
                )}
            </InputAdornment>
        );
    }, [details.loading]);

    return (
        <Box
            className='workboard_details'
            sx={{
                padding: '16px'
            }}
        >
            <Typography
                gutterBottom
                variant='h3'
            >
                {translations.ONLINK_WORKBOARD_DETAILS}
            </Typography>
            <div className='wb-details_container'>
                <Box
                    sx={{
                        marginBottom: '8px'
                    }}
                >
                    <ReadOnlyWrapperComponent
                        label={translations.NAME}
                        readOnly={!hasPermission}
                        readOnlyProps={{
                            readOnlyLabelClassName: 'title-1',
                            readOnlyValueClassName: 'workboard-details-readonly-value'
                        }}
                        value={details.name || ''}
                        wrappedComponent={ValidationInput}
                        wrappedProps={{
                            name: 'name',
                            className: 'wb-details_input',
                            disabled: details.loading,
                            errors: {
                                customError: duplicateValidation.showDuplicateNameError ?
                                    duplicateValidation.duplicateNameMessage :
                                    ''
                            },
                            invalidCustomError: duplicateValidation.showDuplicateNameError,
                            onChange: (event) => handleDetailsChange(event, setNotesError, handleChange),
                            placeholder: details.loading ? translations.LOADING : translations.ENTER_NAME,
                            required: true,
                            setValid,
                            tabIndex: 0,
                            InputProps: {
                                endAdornment: loading
                            },
                            type: 'text'
                        }}
                    />
                </Box>
                <div className='wb-details_dateTime'>
                    <ReadOnlyWrapperComponent
                        label={translations.DATE_AND_TIME}
                        readOnly={!hasPermission}
                        readOnlyProps={{
                            readOnlyLabelClassName: 'title-1',
                            readOnlyValueClassName: 'workboard-details-readonly-value',
                            value: moment(details.date).format('LLL')
                        }}
                        wrappedComponent={DateTimePicker}
                        wrappedProps={{
                            allowEmpty: true,
                            dateProps: {
                                disabled: details.loading || isEditing && !isDraft,
                                sx: {
                                    width: '100%'
                                },
                                validatedDate,
                                hideDatePicker: isTemplate
                            },
                            timeProps: {
                                error: duplicateValidation.showDuplicateStartTimeError,
                                helperText: duplicateValidation.showDuplicateStartTimeError ? duplicateValidation.duplicateStartTimeMessage : '',
                                loading: details.loading,
                                timeFormat: getLocalTimeFormats(),
                                timeIntervals: 15,
                                timeLabel: `${translations.START} ${translations.TIME}`,
                                validatedTime,
                                width: '100%'
                            },
                            gap: '8px',
                            translations,
                            onChange: (date) => {
                                handleChange({
                                    field: 'date',
                                    value: date?.format(MANUAL_DATA_TIME_FORMAT)
                                });
                                handleLoadingOperators();
                                handleUpdateTeamMembers({
                                    orgId: membership?.orgId,
                                    date: date.toDate(),
                                    tagOnTimeOff: true,
                                    refreshSelected: true
                                });
                            }
                        }}
                    />
                </div>
                <div className='wb-details_pinLocation'>
                    <PinLocation
                        adornment={loading}
                        column={true}
                        disabled={details.loading}
                        onChange={(event) => handleDetailsChange(event, setNotesError, handleChange)}
                        pinLocation={details.pinLocation}
                        placeholder={translations.ONLINK_DETAILS_PIN_PLACEHOLDER}
                        readOnly={!hasPermission}
                        setValid={setValid}
                        translations={translations}
                        workboards={workboards}
                    />
                </div>
                <div className='wb-details_notes'>
                    <ReadOnlyWrapperComponent
                        label={translations.NOTES}
                        readOnly={!hasPermission}
                        readOnlyProps={{
                            readOnlyLabelClassName: 'title-1',
                            readOnlyValueClassName: 'workboard-details-readonly-value'
                        }}
                        value={details.notes || ''}
                        wrappedComponent={TextField}
                        wrappedProps={{
                            multiline: true,
                            className: 'notes-text-area',
                            disabled: details.loading,
                            error: notesError,
                            name: 'notes',
                            minRows: 8,
                            onChange: (event) => handleDetailsChange(event, setNotesError, handleChange),
                            InputProps: {
                                endAdornment: loading
                            },
                            placeholder: details.loading ? translations.LOADING : translations.CONSTRUCTION_ENTER_NOTES,
                            helperText: replaceTranslationNames(translations.ONLINK_SMS_CHAR_REMAINING, {
                                '0': details.notes.length > MAX_NOTE_LENGTH ? 0 : MAX_NOTE_LENGTH - details.notes.length,
                                '1': MAX_NOTE_LENGTH.toString()
                            }),
                            tabIndex: 0
                        }}
                    />
                </div>
            </div>
        </Box>
    );
};

WorkboardDetails.propTypes = {
    details: PropTypes.object,
    handleChange: PropTypes.func,
    handleLoadingOperators: PropTypes.func,
    handleNameValidation: PropTypes.func,
    handleUpdateTeamMembers: PropTypes.func,
    isCopy: PropTypes.bool,
    isDraft: PropTypes.bool,
    isEditing: PropTypes.bool,
    loading: PropTypes.bool,
    membership: PropTypes.membership,
    membershipId: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    nameValidation: PropTypes.object,
    setValid: PropTypes.func,
    translations: PropTypes.translations,
    type: PropTypes.string,
    workboardId: PropTypes.string
};

export function mapStateToProps(state) {
    return {
        details: state.setupWorkboard.details,
        isCopy: state.setupWorkboard.isCopy,
        isEditing: state.setupWorkboard.isEditing,
        isDraft: state.setupWorkboard.isDraft,
        nameValidation: state.setupWorkboard.nameValidation,
        membership: state.membership,
        membershipId: state.membership.membershipId,
        myJdPermissions: state.account.myJdPermissions,
        workboardId: state.setupWorkboard.workboardId
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        handleChange(props) {
            dispatch(updateDetailsField(props));
        },
        handleLoadingOperators(props) {
            dispatch(setLoadingOperators(props));
        },
        handleNameValidation(value) {
            dispatch(setNameValidation(value));
        },
        handleUpdateTeamMembers(props) {
            dispatch(fetchTeamMembers(props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(WorkboardDetails));
